import React from "react";
import PropTypes from "prop-types";
import { graphql } from "@apollo/client/react/hoc";
import compose from "lodash.flowright";
import { withTranslation } from "react-i18next";

import { GET_WINE_FILTERS, GET_AUTH } from "../../../graphql/queries";
import {
  FETCH_POLICY_CACHE_ONLY,
  CELLAR_WINE_SORTER_ARR,
  I18N_NAMESPACES,
  WINE_TYPE_IDS,
  WINE_FILTERS as FILTERS,
} from "../../../helpers/constants";
import { getWineSortingOptions } from "../../../helpers/wineSorting";

import FilterWinesHeader from "../../atoms/FilterWinesHeader";
import FilterWinesFooter from "../../atoms/FilterWinesFooter";
import StyledButtonWithUpAndDownArrows from "../../atoms/StyledButtonWithUpAndDownArrows";

import Loading from "../../atoms/Loading";
import RadioInRight from "../../atoms/RadioInRight";
import MultiRangeSlider from "../../atoms/MultiRangeSlider";
import {
  FilterWinesHolderContainer,
  FilterWinesBodyHolder,
  FilterWinesCheckboxFilters,
  FilterWinesRangeFilters,
  FilterWinesTitle,
  FilterItem,
  SmallerCheckboxFilter,
  PricePointsCheckbox,
  InterrogationRadioComponent,
  FilterTitle,
  loadingStyle,
  BackButton,
  Cross,
  MatchCountContainer,
} from "./style";

const defaultState = {
  winePriceRangeIds: [],
  wineClassIds: [],
  wineProductionMethodIds: [],
  cuisineIds: [],
  countryIds: [],
  wineRegionIds: [],
  wineTypeIds: [],
  wineAlcoholRange: [],
  wineListSlug: undefined,
  wineSweetnessId: undefined,
  wineTanninId: undefined,
  wineBodyId: undefined,
  wineFruitDrivenId: undefined,
  wineAcidityId: undefined,
  wineOakyId: undefined,
  orderBy: undefined,
  filterCategories: new Map([
    [FILTERS.PRICE_RANGE, true],
    [FILTERS.COLOUR, true],
    [FILTERS.COUNTRY, false],
    [FILTERS.REGION, false],
    [FILTERS.DRY_SWEET, false],
    [FILTERS.TANNIN, false],
    [FILTERS.BODY, false],
    [FILTERS.FRUITINESS, false],
    [FILTERS.ACIDITY, false],
    [FILTERS.OAKY, false],
    [FILTERS.OTHER, false],
    [FILTERS.GRAPE_TYPE, false],
    [FILTERS.ALCOHOL_RANGE, false],
    [FILTERS.COLLECTIONS, false],
    [FILTERS.CUISINE, false],
    [FILTERS.ORDER_BY, false],
  ]),
};

class FilterWinesHolder extends React.Component {
  constructor(props) {
    super(props);

    // Overrides state initialization with props received from parent component
    const {
      selectedWinePriceRangeIds,
      selectedWineClassIds,
      selectedWineProductionMethodIds,
      selectedCountryIds,
      selectedWineRegionIds,
      selectedWineTypeIds,
      selectedWineAlcoholRange,
      selectedWineSweetnessId,
      selectedWineTanninId,
      selectedWineBodyId,
      selectedWineFruitDrivenId,
      selectedWineAcidityId,
      selectedWineOakyId,
      selectedWineListSlug,
      selectedCuisineIds,
      selectedOrderBy,
    } = this.props;

    this.state = {
      ...defaultState,
      winePriceRangeIds: selectedWinePriceRangeIds,
      wineClassIds: selectedWineClassIds,
      wineProductionMethodIds: selectedWineProductionMethodIds,
      countryIds: selectedCountryIds,
      wineRegionIds: selectedWineRegionIds,
      wineTypeIds: selectedWineTypeIds,
      wineAlcoholRange: selectedWineAlcoholRange,
      wineSweetnessId: selectedWineSweetnessId,
      wineTanninId: selectedWineTanninId,
      wineBodyId: selectedWineBodyId,
      wineFruitDrivenId: selectedWineFruitDrivenId,
      wineAcidityId: selectedWineAcidityId,
      wineOakyId: selectedWineOakyId,
      wineListSlug: selectedWineListSlug,
      cuisineIds: selectedCuisineIds,
      orderBy: selectedOrderBy,
    };
  }

  handlePriceRangeSelection = (newId) => {
    const { onChange } = this.props;
    this.setState(
      (prevState) => {
        let selectedPriceRanges = [...prevState.winePriceRangeIds];
        if (selectedPriceRanges.includes(newId)) {
          selectedPriceRanges = selectedPriceRanges.filter((priceRangeId) => priceRangeId !== newId);
        } else {
          selectedPriceRanges.push(newId);
        }
        return { winePriceRangeIds: selectedPriceRanges };
      },
      () => {
        const filterStates = this.getFormattedFilterStatesForMutation();
        onChange({ ...filterStates });
      }
    );
  };

  handleMultipleSelection = (newId, stateName, unselect) => {
    // Unselects all filters if selecting Orange Wine.
    if (newId === WINE_TYPE_IDS.DB_ID_WINE_TYPE_ORANGE_WINE && stateName === FILTERS.GRAPE_TYPE) {
      this.setState(() => ({ [FILTERS.COLOUR]: [] }));
    }

    // Unselects Orange Wine if selecting red, white, etc.
    if (newId !== WINE_TYPE_IDS.DB_ID_WINE_TYPE_ORANGE_WINE && stateName === FILTERS.COLOUR) {
      this.setState((previousState) => ({
        [FILTERS.GRAPE_TYPE]: [
          ...previousState[FILTERS.GRAPE_TYPE].filter(
            (id) => id !== WINE_TYPE_IDS.DB_ID_WINE_TYPE_ORANGE_WINE
          ),
        ],
      }));
    }

    const { onChange } = this.props;
    this.setState(
      (previousState) => {
        if (unselect) {
          return {
            [stateName]: [...previousState[stateName].filter((id) => id !== newId)],
          };
        }
        return { [stateName]: [...previousState[stateName], newId] };
      },
      () => {
        const filterStates = this.getFormattedFilterStatesForMutation();
        onChange({ ...filterStates });
      }
    );
  };

  handleAlcoholRangeSelection = (value) => {
    const { onChange } = this.props;
    this.setState({ wineAlcoholRange: value }, () => {
      const filterStates = this.getFormattedFilterStatesForMutation();
      onChange({ ...filterStates });
    });
  };

  handleUniqueSelection = (newId, stateName) => {
    const { onChange } = this.props;
    this.setState({ [stateName]: newId }, () => {
      const filterStates = this.getFormattedFilterStatesForMutation();
      onChange({ ...filterStates, [stateName]: newId });
    });
  };

  /**
   * Gets the reformatted filter object and calls the appropriate onChange function
   * for filters in either Cellar or All Wines.
   * */
  callbackAfterClearingFilters = () => {
    const { onChange, isInMyCellar } = this.props;
    const filterStates = this.getFormattedFilterStatesForMutation();
    if (isInMyCellar) {
      const isClearAllFilters = true;
      onChange({ ...filterStates }, isClearAllFilters);
    } else {
      onChange({ ...filterStates, name: "" });
    }
  };

  /**
   * Clears all selected filters and search field.
   * */
  handleClearFilters = () => {
    this.setState({ ...defaultState }, () => this.callbackAfterClearingFilters());
  };

  removeWord = (fullWord, word) => fullWord.replace(word, "");

  /**
   * Removes the 'filterCategories' field from 'this.state' object,
   * since, its not used in the mutation to filter wines.
   * */
  getFormattedFilterStatesForMutation = () => {
    const { selectedName } = this.props;

    // state "filterCategories" is only used for the collapsible categories and not used in mutation.
    const { filterCategories, ...filterStates } = this.state;
    // search is the new field name used for product-related queries
    return { ...filterStates, name: selectedName, search: selectedName };
  };

  /**
   * Formats wine lists in the format {id, name} to pass to filter dropdown.
   *
   * @returns {Array}
   * */
  formatAllWineLists = () => {
    const { wineFiltersQuery } = this.props;
    const { allWineLists } = wineFiltersQuery;
    return [
      ...allWineLists.map((wineList) => ({
        id: wineList.slug,
        name: wineList.name,
      })),
    ];
  };

  /**
   * Toggles the hide and show of filters of various categories.
   *
   * @param {String} field
   * */
  toggleCategory = (field) => {
    const { filterCategories } = this.state;
    const boolean = filterCategories.get(field);
    if (boolean !== undefined) {
      filterCategories.set(field, !boolean);
      this.setState({ filterCategories });
    }
  };

  /**
   * Returns the appropriate sort options for members and non-logged-in customers.
   *
   * @returns {Array}
   * */
  getSortingOptions = () => {
    const { authQuery, isInMyCellar, t } = this.props;
    if (isInMyCellar) {
      return CELLAR_WINE_SORTER_ARR;
    }
    const { memberId } = authQuery.auth || {};
    return getWineSortingOptions(t, !!memberId);
  };

  render() {
    const {
      filterWinesIsOpen,
      toggleFilterWines,
      className,
      wineFiltersQuery,
      showPriceRangesFilter,
      showWineColoursFilter,
      showWineProductionMethodsFilter,
      showWineCountriesFilter,
      showWineRegionsFilter,
      showWineGrapeTypesFilter,
      showWineAlcoholFilter,
      showWineSweetnessesFilter,
      showWineTanninsFilter,
      showWineBodiesFilter,
      showWineFruitDrivensFilter,
      showWineAciditiesFilter,
      showWineOakiesFilter,
      showWineCollectionsFilter,
      showCuisinesFilter,
      showOrderByFilter,
      totalMatchCount,
      matchCountData,
      matchCountLoading,
      t,
    } = this.props;
    const {
      winePriceRangeIds,
      wineClassIds,
      wineProductionMethodIds,
      cuisineIds,
      countryIds,
      wineRegionIds,
      wineTypeIds,
      wineAlcoholRange,
      wineSweetnessId,
      wineTanninId,
      wineBodyId,
      wineFruitDrivenId,
      wineAcidityId,
      wineOakyId,
      wineListSlug,
      orderBy,
      filterCategories,
    } = this.state;

    if (wineFiltersQuery.loading) {
      return <Loading style={loadingStyle} />;
    }

    const allWineLists = this.formatAllWineLists();

    const {
      allWinePriceRanges,
      allWineClasses,
      allWineProductionMethods,
      allCuisines,
      allWineCountries,
      allWineRegions,
      allWineTypes,
      allWineSweetnesses,
      allWineTannins,
      allWineBodies,
      allWineFruitDrivens,
      allWineAcidities,
      allWineOakies,
    } = wineFiltersQuery;

    const orderByOptions = this.getSortingOptions();

    // Build lists of filters with at least one match
    const filtersWithMatches = {};
    Object.keys(matchCountData || {}).forEach((key) => {
      filtersWithMatches[key] = matchCountData[key].reduce(
        (previous, item) => ({ ...previous, [item.id]: item.matchCount }),
        {}
      );
    });

    const renderMatchCount = (filterTypeKey, filterId) => {
      if (!matchCountData && !matchCountLoading) return null;

      return (
        <>
          {" "}
          <MatchCountContainer>
            {matchCountLoading ? "-" : filtersWithMatches?.[filterTypeKey]?.[filterId] || 0}
          </MatchCountContainer>
        </>
      );
    };

    return (
      <FilterWinesHolderContainer filterWinesIsOpen={filterWinesIsOpen} className={className}>
        <FilterWinesHeader toggleFilterWines={toggleFilterWines} />

        <FilterWinesBodyHolder>
          <FilterTitle>
            <BackButton onClick={() => toggleFilterWines()}>
              <Cross />
            </BackButton>
            <FilterWinesTitle>{t("winePages:filters")}</FilterWinesTitle>
          </FilterTitle>

          {showPriceRangesFilter && (
            <FilterItem isOpen={filterCategories.get(FILTERS.PRICE_RANGE) || false}>
              <StyledButtonWithUpAndDownArrows
                onClick={() => this.toggleCategory(FILTERS.PRICE_RANGE)}
                title={t("common:price")}
                isOpen={filterCategories.get(FILTERS.PRICE_RANGE) || false}
              />
              {filterCategories.get(FILTERS.PRICE_RANGE) && (
                <PricePointsCheckbox type="checkbox" onChange={this.handlePriceRangeSelection}>
                  {allWinePriceRanges && allWinePriceRanges.length
                    ? allWinePriceRanges.map((priceRange) => (
                        <InterrogationRadioComponent
                          key={priceRange.id}
                          value={priceRange.id}
                          label={t("common:winePriceRange", { context: priceRange.id })}
                          forceChecked={winePriceRangeIds.includes(priceRange.id)}
                        />
                      ))
                    : []}
                </PricePointsCheckbox>
              )}
            </FilterItem>
          )}

          {showWineColoursFilter && (
            <FilterItem isOpen={filterCategories.get(FILTERS.COLOUR) || false}>
              <StyledButtonWithUpAndDownArrows
                onClick={() => this.toggleCategory(FILTERS.COLOUR)}
                title={t("common:colour")}
                isOpen={filterCategories.get(FILTERS.COLOUR) || false}
              />
              {filterCategories.get(FILTERS.COLOUR) && (
                <FilterWinesCheckboxFilters>
                  {allWineClasses &&
                    allWineClasses.map((wineClass) => (
                      <SmallerCheckboxFilter
                        multipleSelection
                        key={wineClass.id}
                        value={wineClass.id}
                        isChecked={wineClassIds.includes(wineClass.id)}
                        stateName={FILTERS.COLOUR}
                        onChange={this.handleMultipleSelection}
                      >
                        {wineClass.name}
                        {renderMatchCount("allWinesByWineListClassCount", wineClass.id)}
                      </SmallerCheckboxFilter>
                    ))}
                  <SmallerCheckboxFilter
                    multipleSelection
                    key="wine-type-orange-wine"
                    value={WINE_TYPE_IDS.DB_ID_WINE_TYPE_ORANGE_WINE}
                    isChecked={wineTypeIds.includes(WINE_TYPE_IDS.DB_ID_WINE_TYPE_ORANGE_WINE)}
                    stateName={FILTERS.GRAPE_TYPE}
                    onChange={this.handleMultipleSelection}
                  >
                    {t("common:orange")}
                    {renderMatchCount(
                      "allWinesByWineListTypeCount",
                      WINE_TYPE_IDS.DB_ID_WINE_TYPE_ORANGE_WINE
                    )}
                  </SmallerCheckboxFilter>
                </FilterWinesCheckboxFilters>
              )}
            </FilterItem>
          )}

          {showOrderByFilter && (
            <FilterItem isOpen={filterCategories.get(FILTERS.ORDER_BY) || false}>
              <StyledButtonWithUpAndDownArrows
                onClick={() => this.toggleCategory(FILTERS.ORDER_BY)}
                title={t("winePages:sortBy")}
                isOpen={filterCategories.get(FILTERS.ORDER_BY) || false}
              />
              {filterCategories.get(FILTERS.ORDER_BY) && (
                <FilterWinesCheckboxFilters>
                  {orderByOptions &&
                    orderByOptions.map((option, index) => (
                      <RadioInRight
                        key={option.id}
                        id={option.id}
                        stateName={FILTERS.ORDER_BY}
                        onRadioClick={this.handleUniqueSelection}
                        label={option.name}
                        isSelected={orderBy === option.id}
                        hideBottomBorder={orderByOptions && orderByOptions.length - 1 === index}
                      />
                    ))}
                </FilterWinesCheckboxFilters>
              )}
            </FilterItem>
          )}

          {showWineGrapeTypesFilter && (
            <FilterItem isOpen={filterCategories.get(FILTERS.GRAPE_TYPE)}>
              <StyledButtonWithUpAndDownArrows
                onClick={() => this.toggleCategory(FILTERS.GRAPE_TYPE)}
                title={t("common:grapeType")}
                isOpen={filterCategories.get(FILTERS.GRAPE_TYPE)}
              />
              {filterCategories.get(FILTERS.GRAPE_TYPE) && (
                <FilterWinesCheckboxFilters>
                  {allWineTypes &&
                    allWineTypes.map((wineType) => (
                      <SmallerCheckboxFilter
                        multipleSelection
                        key={wineType.id}
                        value={wineType.id}
                        isChecked={wineTypeIds.includes(wineType.id)}
                        stateName={FILTERS.GRAPE_TYPE}
                        onChange={this.handleMultipleSelection}
                      >
                        {wineType.name}
                        {renderMatchCount("allWinesByWineListTypeCount", wineType.id)}
                      </SmallerCheckboxFilter>
                    ))}
                </FilterWinesCheckboxFilters>
              )}
            </FilterItem>
          )}

          {showWineCollectionsFilter && (
            <FilterItem isOpen={filterCategories.get(FILTERS.COLLECTIONS) || false}>
              <StyledButtonWithUpAndDownArrows
                onClick={() => this.toggleCategory(FILTERS.COLLECTIONS)}
                title={t("common:collections")}
                isOpen={filterCategories.get(FILTERS.COLLECTIONS) || false}
              />
              {filterCategories.get(FILTERS.COLLECTIONS) && (
                <FilterWinesCheckboxFilters>
                  {allWineLists &&
                    allWineLists.map((wineList) => (
                      <SmallerCheckboxFilter
                        key={wineList.id}
                        value={wineList.id}
                        isChecked={wineListSlug === wineList.id}
                        stateName={FILTERS.COLLECTIONS}
                        onChange={this.handleUniqueSelection}
                      >
                        {wineList.name}
                        {renderMatchCount("allWinesByWineListWineListCount", wineList.id)}
                      </SmallerCheckboxFilter>
                    ))}
                </FilterWinesCheckboxFilters>
              )}
            </FilterItem>
          )}

          {showWineRegionsFilter && (
            <FilterItem isOpen={filterCategories.get(FILTERS.REGION) || false}>
              <StyledButtonWithUpAndDownArrows
                onClick={() => this.toggleCategory(FILTERS.REGION)}
                title={t("common:regions")}
                isOpen={filterCategories.get(FILTERS.REGION) || false}
              />
              {filterCategories.get(FILTERS.REGION) && (
                <FilterWinesCheckboxFilters>
                  {allWineRegions &&
                    allWineRegions.map((wineRegion) => (
                      <SmallerCheckboxFilter
                        multipleSelection
                        key={wineRegion.id}
                        value={wineRegion.id}
                        isChecked={wineRegionIds.includes(wineRegion.id)}
                        stateName={FILTERS.REGION}
                        onChange={this.handleMultipleSelection}
                      >
                        {wineRegion.name}
                        {renderMatchCount("allWinesByWineListRegionCount", wineRegion.id)}
                      </SmallerCheckboxFilter>
                    ))}
                </FilterWinesCheckboxFilters>
              )}
            </FilterItem>
          )}

          {showWineCountriesFilter && (
            <FilterItem isOpen={filterCategories.get(FILTERS.COUNTRY) || false}>
              <StyledButtonWithUpAndDownArrows
                onClick={() => this.toggleCategory(FILTERS.COUNTRY)}
                title={t("common:country")}
                isOpen={filterCategories.get(FILTERS.COUNTRY) || false}
              />
              {filterCategories.get(FILTERS.COUNTRY) && (
                <FilterWinesCheckboxFilters>
                  {allWineCountries &&
                    allWineCountries.map((wineCountry) => (
                      <SmallerCheckboxFilter
                        multipleSelection
                        key={wineCountry.id}
                        value={wineCountry.id}
                        isChecked={countryIds.includes(wineCountry.id)}
                        stateName={FILTERS.COUNTRY}
                        onChange={this.handleMultipleSelection}
                      >
                        {wineCountry.name}
                        {renderMatchCount("allWinesByWineListCountryCount", wineCountry.id)}
                      </SmallerCheckboxFilter>
                    ))}
                </FilterWinesCheckboxFilters>
              )}
            </FilterItem>
          )}

          {showWineProductionMethodsFilter && (
            <FilterItem isOpen={filterCategories.get(FILTERS.OTHER) || false}>
              <StyledButtonWithUpAndDownArrows
                onClick={() => this.toggleCategory(FILTERS.OTHER)}
                title={t("common:productionMethod")}
                isOpen={filterCategories.get(FILTERS.OTHER) || false}
              />
              {filterCategories.get(FILTERS.OTHER) && (
                <FilterWinesCheckboxFilters>
                  {allWineProductionMethods &&
                    allWineProductionMethods.map((wineProductionMethod) => (
                      <SmallerCheckboxFilter
                        multipleSelection
                        key={wineProductionMethod.id}
                        value={wineProductionMethod.id}
                        isChecked={wineProductionMethodIds.includes(wineProductionMethod.id)}
                        stateName={FILTERS.OTHER}
                        onChange={this.handleMultipleSelection}
                      >
                        {wineProductionMethod.name}
                        {renderMatchCount("allWinesByWineListProductionMethodCount", wineProductionMethod.id)}
                      </SmallerCheckboxFilter>
                    ))}
                </FilterWinesCheckboxFilters>
              )}
            </FilterItem>
          )}

          {showCuisinesFilter && (
            <FilterItem isOpen={filterCategories.get(FILTERS.CUISINE) || false}>
              <StyledButtonWithUpAndDownArrows
                onClick={() => this.toggleCategory(FILTERS.CUISINE)}
                title={t("common:cuisine")}
                isOpen={filterCategories.get(FILTERS.CUISINE) || false}
              />
              {filterCategories.get(FILTERS.CUISINE) && (
                <FilterWinesCheckboxFilters>
                  {allCuisines &&
                    allCuisines.map((cuisine) => (
                      <SmallerCheckboxFilter
                        multipleSelection
                        key={cuisine.id}
                        value={cuisine.id}
                        isChecked={cuisineIds.includes(cuisine.id)}
                        stateName={FILTERS.CUISINE}
                        onChange={this.handleMultipleSelection}
                      >
                        {cuisine.name}
                        {renderMatchCount("allWinesByWineListCuisineCount", cuisine.id)}
                      </SmallerCheckboxFilter>
                    ))}
                </FilterWinesCheckboxFilters>
              )}
            </FilterItem>
          )}

          {showWineSweetnessesFilter && (
            <FilterItem isOpen={filterCategories.get(FILTERS.DRY_SWEET) || false}>
              <StyledButtonWithUpAndDownArrows
                onClick={() => this.toggleCategory(FILTERS.DRY_SWEET)}
                title={t("winePages:drySweet")}
                isOpen={filterCategories.get(FILTERS.DRY_SWEET) || false}
              />
              {filterCategories.get(FILTERS.DRY_SWEET) && (
                <FilterWinesCheckboxFilters>
                  {allWineSweetnesses &&
                    allWineSweetnesses.map((wineSweet) => (
                      <SmallerCheckboxFilter
                        key={wineSweet.id}
                        value={wineSweet.id}
                        isChecked={wineSweetnessId === wineSweet.id}
                        stateName={FILTERS.DRY_SWEET}
                        onChange={this.handleUniqueSelection}
                      >
                        {this.removeWord(wineSweet.name, "Sweetness")}
                        {renderMatchCount("allWinesByWineListSweetnessCount", wineSweet.id)}
                      </SmallerCheckboxFilter>
                    ))}
                </FilterWinesCheckboxFilters>
              )}
            </FilterItem>
          )}

          {showWineTanninsFilter && (
            <FilterItem isOpen={filterCategories.get(FILTERS.TANNIN) || false}>
              <StyledButtonWithUpAndDownArrows
                onClick={() => this.toggleCategory(FILTERS.TANNIN)}
                title={t("winePages:tannins")}
                isOpen={filterCategories.get(FILTERS.TANNIN) || false}
              />
              {filterCategories.get(FILTERS.TANNIN) && (
                <FilterWinesCheckboxFilters>
                  {allWineTannins &&
                    allWineTannins.map((wineTannin) => (
                      <SmallerCheckboxFilter
                        key={wineTannin.id}
                        value={wineTannin.id}
                        isChecked={wineTanninId === wineTannin.id}
                        stateName={FILTERS.TANNIN}
                        onChange={this.handleUniqueSelection}
                      >
                        {this.removeWord(wineTannin.name, "Tannins")}
                        {renderMatchCount("allWinesByWineListTanninCount", wineTannin.id)}
                      </SmallerCheckboxFilter>
                    ))}
                </FilterWinesCheckboxFilters>
              )}
            </FilterItem>
          )}

          {showWineAlcoholFilter && (
            <FilterItem isOpen={filterCategories.get(FILTERS.ALCOHOL_RANGE)}>
              <StyledButtonWithUpAndDownArrows
                onClick={() => this.toggleCategory(FILTERS.ALCOHOL_RANGE)}
                title={t("common:alcoholRange")}
                isOpen={filterCategories.get(FILTERS.ALCOHOL_RANGE)}
              />
              {filterCategories.get(FILTERS.ALCOHOL_RANGE) && (
                <FilterWinesRangeFilters>
                  <MultiRangeSlider
                    min={0}
                    max={50}
                    value={wineAlcoholRange?.length ? wineAlcoholRange : [0, 50]}
                    minDistance={1}
                    onChange={this.handleAlcoholRangeSelection}
                    renderValueInThumb={(val) => `${val}%`}
                  />
                </FilterWinesRangeFilters>
              )}
            </FilterItem>
          )}

          {showWineBodiesFilter && (
            <FilterItem isOpen={filterCategories.get(FILTERS.BODY) || false}>
              <StyledButtonWithUpAndDownArrows
                onClick={() => this.toggleCategory(FILTERS.BODY)}
                title={t("winePages:body")}
                isOpen={filterCategories.get(FILTERS.BODY) || false}
              />
              {filterCategories.get(FILTERS.BODY) && (
                <FilterWinesCheckboxFilters>
                  {allWineBodies &&
                    allWineBodies.map((wineBody) => (
                      <SmallerCheckboxFilter
                        key={wineBody.id}
                        value={wineBody.id}
                        isChecked={wineBodyId === wineBody.id}
                        stateName={FILTERS.BODY}
                        onChange={this.handleUniqueSelection}
                      >
                        {wineBody.shortName}
                        {renderMatchCount("allWinesByWineListBodyCount", wineBody.id)}
                      </SmallerCheckboxFilter>
                    ))}
                </FilterWinesCheckboxFilters>
              )}
            </FilterItem>
          )}

          {showWineFruitDrivensFilter && (
            <FilterItem isOpen={filterCategories.get(FILTERS.FRUITINESS) || false}>
              <StyledButtonWithUpAndDownArrows
                onClick={() => this.toggleCategory(FILTERS.FRUITINESS)}
                title={t("winePages:fruitiness")}
                isOpen={filterCategories.get(FILTERS.FRUITINESS) || false}
              />
              {filterCategories.get(FILTERS.FRUITINESS) && (
                <FilterWinesCheckboxFilters>
                  {allWineFruitDrivens &&
                    allWineFruitDrivens.map((wineFruitDriven) => (
                      <SmallerCheckboxFilter
                        key={wineFruitDriven.id}
                        value={wineFruitDriven.id}
                        isChecked={wineFruitDrivenId === wineFruitDriven.id}
                        stateName={FILTERS.FRUITINESS}
                        onChange={this.handleUniqueSelection}
                      >
                        {wineFruitDriven.name}
                        {renderMatchCount("allWinesByWineListFruitDrivenCount", wineFruitDriven.id)}
                      </SmallerCheckboxFilter>
                    ))}
                </FilterWinesCheckboxFilters>
              )}
            </FilterItem>
          )}

          {showWineAciditiesFilter && (
            <FilterItem isOpen={filterCategories.get(FILTERS.ACIDITY) || false}>
              <StyledButtonWithUpAndDownArrows
                onClick={() => this.toggleCategory(FILTERS.ACIDITY)}
                title={t("winePages:acidity")}
                isOpen={filterCategories.get(FILTERS.ACIDITY) || false}
              />
              {filterCategories.get(FILTERS.ACIDITY) && (
                <FilterWinesCheckboxFilters>
                  {allWineAcidities &&
                    allWineAcidities.map((wineAcidity) => (
                      <SmallerCheckboxFilter
                        key={wineAcidity.id}
                        value={wineAcidity.id}
                        isChecked={wineAcidityId === wineAcidity.id}
                        stateName={FILTERS.ACIDITY}
                        onChange={this.handleUniqueSelection}
                      >
                        {this.removeWord(wineAcidity.name, "Acidity")}
                        {renderMatchCount("allWinesByWineListAcidityCount", wineAcidity.id)}
                      </SmallerCheckboxFilter>
                    ))}
                </FilterWinesCheckboxFilters>
              )}
            </FilterItem>
          )}

          {showWineOakiesFilter && (
            <FilterItem isOpen={filterCategories.get(FILTERS.OAKY) || false}>
              <StyledButtonWithUpAndDownArrows
                onClick={() => this.toggleCategory(FILTERS.OAKY)}
                title={t("winePages:oak")}
                isOpen={filterCategories.get(FILTERS.OAKY) || false}
              />
              {filterCategories.get(FILTERS.OAKY) && (
                <FilterWinesCheckboxFilters>
                  {allWineOakies &&
                    allWineOakies.map((wineOaky) => (
                      <SmallerCheckboxFilter
                        key={wineOaky.id}
                        value={wineOaky.id}
                        isChecked={wineOakyId === wineOaky.id}
                        stateName={FILTERS.OAKY}
                        onChange={this.handleUniqueSelection}
                      >
                        {wineOaky.name}
                        {renderMatchCount("allWinesByWineListOakyCount", wineOaky.id)}
                      </SmallerCheckboxFilter>
                    ))}
                </FilterWinesCheckboxFilters>
              )}
            </FilterItem>
          )}
        </FilterWinesBodyHolder>
        <FilterWinesFooter
          toggleFilterWines={toggleFilterWines}
          clearFilter={this.handleClearFilters}
          doneButtonContent={
            <>
              {t("common:done")}
              {Number.isInteger(totalMatchCount) ? ` (${totalMatchCount})` : ""}
            </>
          }
        />
      </FilterWinesHolderContainer>
    );
  }
}
FilterWinesHolder.propTypes = {
  filterWinesIsOpen: PropTypes.bool,
  toggleFilterWines: PropTypes.func,
  className: PropTypes.string,
  onChange: PropTypes.func,
  t: PropTypes.func.isRequired,

  showPriceRangesFilter: PropTypes.bool,
  showWineColoursFilter: PropTypes.bool,
  showWineProductionMethodsFilter: PropTypes.bool,
  showWineCountriesFilter: PropTypes.bool,
  showWineRegionsFilter: PropTypes.bool,
  showWineGrapeTypesFilter: PropTypes.bool,
  showWineAlcoholFilter: PropTypes.bool,
  showWineSweetnessesFilter: PropTypes.bool,
  showWineTanninsFilter: PropTypes.bool,
  showWineBodiesFilter: PropTypes.bool,
  showWineFruitDrivensFilter: PropTypes.bool,
  showWineAciditiesFilter: PropTypes.bool,
  showWineOakiesFilter: PropTypes.bool,
  showWineCollectionsFilter: PropTypes.bool,
  showCuisinesFilter: PropTypes.bool,
  showOrderByFilter: PropTypes.bool,

  authQuery: PropTypes.shape({}).isRequired,
  wineFiltersQuery: PropTypes.shape({}).isRequired,

  selectedWinePriceRangeIds: PropTypes.arrayOf(PropTypes.number),
  selectedWineClassIds: PropTypes.arrayOf(PropTypes.number),
  selectedWineProductionMethodIds: PropTypes.arrayOf(PropTypes.number),
  selectedCuisineIds: PropTypes.arrayOf(PropTypes.number),
  selectedCountryIds: PropTypes.arrayOf(PropTypes.number),
  selectedWineRegionIds: PropTypes.arrayOf(PropTypes.number),
  selectedWineTypeIds: PropTypes.arrayOf(PropTypes.number),
  selectedWineAlcoholRange: PropTypes.arrayOf(PropTypes.number),
  selectedWineSweetnessId: PropTypes.number,
  selectedWineTanninId: PropTypes.number,
  selectedWineBodyId: PropTypes.number,
  selectedWineFruitDrivenId: PropTypes.number,
  selectedWineAcidityId: PropTypes.number,
  selectedWineOakyId: PropTypes.number,
  selectedWineListSlug: PropTypes.string,
  selectedOrderBy: PropTypes.string,
  selectedName: PropTypes.string,
  isInMyCellar: PropTypes.bool.isRequired,
  totalMatchCount: PropTypes.number,
  matchCountData: PropTypes.any,
  matchCountLoading: PropTypes.bool,
};

FilterWinesHolder.defaultProps = {
  filterWinesIsOpen: false,
  toggleFilterWines: () => {},
  className: "",
  onChange: () => {},

  showPriceRangesFilter: true,
  showWineColoursFilter: true,
  showWineProductionMethodsFilter: true,
  showWineCountriesFilter: true,
  showWineRegionsFilter: true,
  showWineGrapeTypesFilter: true,
  showWineAlcoholFilter: true,
  showWineSweetnessesFilter: true,
  showWineTanninsFilter: true,
  showWineBodiesFilter: true,
  showWineFruitDrivensFilter: true,
  showWineAciditiesFilter: true,
  showWineOakiesFilter: true,
  showWineCollectionsFilter: true,
  showCuisinesFilter: true,
  showOrderByFilter: true,

  selectedWinePriceRangeIds: [],
  selectedWineClassIds: [],
  selectedWineProductionMethodIds: [],
  selectedCuisineIds: [],
  selectedCountryIds: [],
  selectedWineRegionIds: [],
  selectedWineTypeIds: [],
  selectedWineAlcoholRange: [],
  selectedWineSweetnessId: undefined,
  selectedWineTanninId: undefined,
  selectedWineBodyId: undefined,
  selectedWineFruitDrivenId: undefined,
  selectedWineAcidityId: undefined,
  selectedWineOakyId: undefined,
  selectedWineListSlug: undefined,
  selectedOrderBy: undefined,
  selectedName: "",
  totalMatchCount: undefined,
  matchCountData: undefined,
  matchCountLoading: {},
};

export default compose(
  graphql(GET_AUTH, {
    name: "authQuery",
    options: { fetchPolicy: FETCH_POLICY_CACHE_ONLY },
  }),
  graphql(GET_WINE_FILTERS, { name: "wineFiltersQuery" }),
  withTranslation([I18N_NAMESPACES.WINE_PAGES, I18N_NAMESPACES.COMMON, I18N_NAMESPACES.QUIZ])
)(FilterWinesHolder);
